import { FeeCalculationScenario, ProductFeeType } from '@shared/constants/product-fees';
import { formatISO } from 'date-fns/formatISO';
import { ProductFeeStructure } from './bridging-products';
import { WhenPayableCustom, WhenRefundable } from './esis';

export interface DistributionGroupRaw {
  id: number | null;
  name: string | null;
  createdDate: string | null;
  giAgencyNo: string | null;
  principalFrn?: string | null;
  principalFirmName?: string | null;
  parentId: number | null;
  frnNumbers: number[];
  externalClientIds: string[];
  isLenderPanelConfigurationEnabled: boolean;
  isPaymentRouteConfigurationEnabled: boolean;
  isFeeConfigurationEnabled: boolean;
  isComplaintsConfigurationEnabled: boolean;
  isColumnConfigurationEnabled: boolean;
  isFilterPreferencesEnabled: boolean;
}

export interface CreateDistributionGroupRequest {
  name: string;
  parentId: number | null;
  giAgencyNo: string | null;
  principalFrn?: string | null;
  frnNumbers: number[];
  externalClientIds: string[];
}

export interface GroupIdentificationUpdateModel {
  giAgencyNo: string | null;
  principalFrn?: string | null;
  frnNumbers: number[];
  externalClientIds: string[];
}

export interface PrincipalFirm {
  principalFrn: string;
  principalFirmName?: string | null;
}

export interface LendingTypeModel {
  name: string;
  value: boolean;
  enabled: boolean;
  isRestricted: boolean;
  restrictedFrns: number[];
  isSelectedByParent: boolean;
}

export interface DistributionGroupLender {
  lenderName: string;
  lendingTypes: LendingTypeModel[];
}

export interface CustomFeeCalculationScenarioRequest {
  feeName: string;
  feeType: ProductFeeType;
  feeCalculationScenario: FeeCalculationScenario;
  canOverride?: boolean | null;
}

export interface CustomFeeRequest  {
  feeName: string;
  whenPayable: WhenPayableCustom;
  addToLoan: boolean;
  feeAmount?: number | null;
  whenRefundable: WhenRefundable;
  amountRefundable: number;
  feeStructure: ProductFeeStructure;
  lendingTypeId: number | null;
  mortgageTypePurchase: boolean;
  mortgageTypeRemortgage: boolean;
  mortgageTypeProductTransfer: boolean;
  mortgageTypeSecondCharge: boolean;
}

export interface CustomProductSettingsRequest {
  isFeeConfigurationEnabled: boolean;
  deductCashback: boolean;
  isDeductCashbackOverridable?: boolean | null;
  assumedLegalFeeCost?: number | null;
  customFeeCalculationScenarios: CustomFeeCalculationScenarioRequest[];
  customFees: CustomFeeRequest[];
  areCustomFeesOverridable: boolean | null;
  distributionGroupId?: number | null;
  userId?: string | null;
}

export enum DistributionGroupType {
  SubGroup = 'Sub group',
  Automated = 'Automated Principal FRN',
  Manual = 'Manual',
  External = 'External Client Id',
}

export class DistributionGroup {
  private readonly _id: number | null;

  private _name: string | null;

  private readonly _createdDate: Date;

  private _giAgencyNo: string | null;

  private _principalFrn: string | null;

  private readonly _principalFirmName: string | null;

  private readonly _parentId: number | null;

  private _firms: { frnNumber: number; fcaFirmName: string }[] = [];

  private _externalClientIds: string[];

  private readonly _fcaFirmNameLookup: Map<number, string>;

  public isLenderPanelConfigurationEnabled: boolean;
  public isPaymentRouteConfigurationEnabled: boolean;
  public isFeeConfigurationEnabled: boolean;
  public isComplaintsConfigurationEnabled: boolean;
  public isColumnConfigurationEnabled: boolean;
  public isFilterPreferencesEnabled: boolean;

  private constructor(proto: DistributionGroupRaw, fcaFirmNameLookup: Map<number, string>) {
    this._fcaFirmNameLookup = fcaFirmNameLookup;

    this._id = proto.id;
    this._name = proto.name;
    this._giAgencyNo = proto.giAgencyNo;
    this._principalFrn = proto.principalFrn ?? null;
    this._principalFirmName = proto.principalFirmName ?? null;
    this._parentId = proto.parentId;
    this._externalClientIds = proto.externalClientIds;

    this.isLenderPanelConfigurationEnabled = proto.isLenderPanelConfigurationEnabled;
    this.isPaymentRouteConfigurationEnabled = proto.isPaymentRouteConfigurationEnabled;
    this.isFeeConfigurationEnabled = proto.isFeeConfigurationEnabled;
    this.isComplaintsConfigurationEnabled = proto.isComplaintsConfigurationEnabled;
    this.isColumnConfigurationEnabled = proto.isColumnConfigurationEnabled;
    this.isFilterPreferencesEnabled = proto.isFilterPreferencesEnabled;

    this.setFrn(proto.frnNumbers);

    this._createdDate = new Date(proto.createdDate ?? 0);
  }

  public static parse(proto: DistributionGroupRaw, fcaFirmNameLookup: Map<number, string>): DistributionGroup {
    return new DistributionGroup(proto, fcaFirmNameLookup);
  }

  public get id(): number | null {
    return this._id;
  }

  public get name(): string | null {
    return this._name;
  }

  public set name(value: string | null) {
    this._name = value;
  }

  public get createdDate(): Date {
    return this._createdDate;
  }

  public get displayExternalClientIds(): string {
    return this._externalClientIds.join(', ');
  }

  public get giAgencyNo(): string | null {
    return this._giAgencyNo;
  }

  public set giAgencyNo(value: string | null) {
    this._giAgencyNo = value;
  }

  public get principalFrn(): string | null {
    return this._principalFrn;
  }

  public set principalFrn(value: string | null) {
    this._principalFrn = value;
  }

  public get principalFirmName(): string | null {
    return this._principalFirmName;
  }

  public get parentId(): number | null {
    return this._parentId;
  }

  public get firms() {
    return this._firms;
  }

  public get frnNumbers(): number[] {
    return this._firms.map(({ frnNumber }) => frnNumber);
  }

  public get fcaFirmNames(): string[] {
    return this._firms.map(({ fcaFirmName }) => fcaFirmName);
  }

  public get externalClientIds(): string[] {
    return this._externalClientIds;
  }

  public set externalClientIds(value: string[]) {
    this._externalClientIds = value;
  }

  public get typeEnum() {
    const types: DistributionGroupType[] = [];
    if (!!this.parentId) {
      types.push(DistributionGroupType.SubGroup);
    }
    if (this.principalFrn?.length || this.giAgencyNo?.length) {
      types.push(DistributionGroupType.Automated);
    }

    // If not 'Sub group' or 'Automated GI Agency Name' then it is manual
    if (types.length === 0) {
      types.push(DistributionGroupType.Manual);
    }

    if (this.displayExternalClientIds) {
      types.push(DistributionGroupType.External);
    }

    return types;
  }

  public get type(): string {
    return this.typeEnum.join(', ');
  }

  public addFrn(frnNumber: number): void {
    this._firms = [...this._firms, { frnNumber, fcaFirmName: this._fcaFirmNameLookup.get(frnNumber) ?? '' }];
    this.sortFirms();
  }

  public addFrnBulk(frnNumbers: number[]): void {
    this._firms = [
      ...this._firms,
      ...frnNumbers.map(frnNumber => ({ frnNumber, fcaFirmName: this._fcaFirmNameLookup.get(frnNumber) ?? '' })),
    ];
    this.sortFirms();
  }

  public setFrn(frnNumbers: number[]): void {
    this._firms = frnNumbers.map(frnNumber => ({
      frnNumber,
      fcaFirmName: this._fcaFirmNameLookup.get(frnNumber) ?? '',
    }));
    this.sortFirms();
  }

  public deleteFrn(frnToDelete: number): void {
    if (!this._firms.some(({ frnNumber }) => frnNumber === frnToDelete)) {
      throw Error('FRN you are trying to delete is not in FRN numbers list');
    }

    this._firms = this._firms.filter(({ frnNumber }) => frnNumber !== frnToDelete);
  }

  public deleteFrns(frnsToDelete: number[]): void {
    this._firms = this._firms.filter(({ frnNumber }) => !frnsToDelete.includes(frnNumber));
  }

  private sortFirms() {
    this._firms.sort((a, b) => a.fcaFirmName.localeCompare(b.fcaFirmName));
  }

  public clone(): DistributionGroup {
    return new DistributionGroup({
      id: this._id,
      name: this._name,
      giAgencyNo: this._giAgencyNo,
      principalFrn: this._principalFrn,
      principalFirmName: this._principalFirmName,
      parentId: this._parentId,
      frnNumbers: [...this.frnNumbers],
      externalClientIds: [...this.externalClientIds],
      createdDate: formatISO(this._createdDate),
      isLenderPanelConfigurationEnabled: this.isLenderPanelConfigurationEnabled,
      isPaymentRouteConfigurationEnabled: this.isPaymentRouteConfigurationEnabled,
      isFeeConfigurationEnabled: this.isFeeConfigurationEnabled,
      isComplaintsConfigurationEnabled: this.isComplaintsConfigurationEnabled,
      isColumnConfigurationEnabled: this.isColumnConfigurationEnabled,
      isFilterPreferencesEnabled: this.isFilterPreferencesEnabled,
    }, this._fcaFirmNameLookup);
  }
}

export interface Firm {
  fcaFirmName: string;
  frnNumber: number;
  isAvailable: boolean;
}

// Does not contain `isComplainsConfigurationEnabled` because that toggle is handled by the update complaints info endpoint
export interface DistributionGroupEnabledFeaturesRequestModel {
  isLenderPanelConfigurationEnabled: boolean;
  isPaymentRouteConfigurationEnabled: boolean;
  isFeeConfigurationEnabled: boolean;
  isColumnConfigurationEnabled: boolean;
  isFilterPreferencesEnabled: boolean;
}
